/** Velden van de vragenlijst, in de huisstijl.
 *
 *  Dit is het strengste deel van de app: iemand vult dit in op zijn eigen telefoon.
 *  Twee regels zijn hier geen smaak maar noodzaak, en ze blijven staan:
 *   — aanraakvlakken minimaal 44px;
 *   — invoertekst minimaal 16px, want kleiner en iOS Safari zoomt bij het aantikken
 *     in en het scherm verspringt.
 *
 *  Gekozen opties staan in de actiekleur (turkoois). Een fout kleurt het hele veld
 *  zacht rood — betekeniskleur, nooit decoratie.
 */
import React from "react";

const KEUZE_BASIS = {
  position: "relative", display: "flex", alignItems: "center", justifyContent: "center",
  minHeight: "var(--touch-min)", minWidth: "var(--touch-min)",
  padding: "var(--ruimte-4) var(--ruimte-5)", borderRadius: "var(--radius)",
  border: "1px solid var(--border-subtle)", background: "var(--wit)",
  color: "var(--text-body)", cursor: "pointer",
  fontSize: "var(--fs-invoer-mobiel)", fontFamily: "var(--font-tekst)",
  transition: "var(--overgang-kleur)",
};
const KEUZE_AAN = { borderColor: "var(--actie)", background: "var(--actie)", color: "#fff", boxShadow: "var(--schaduw-1)" };

export function ScaleField({ name, value, onChange, min, max, labels, testid, ariaLabel }) {
  const items = [];
  for (let i = min; i <= max; i++) items.push(i);
  const hasLabels = labels && (labels.min || labels.max);
  return (
    <div style={{ marginTop: "var(--ruimte-5)" }} data-testid={testid}>
      {/* Een 0-10-schaal (elf opties) brak op een 360px-scherm over drie rijen en las
          dan niet meer als schaal. Daarom een raster dat zich vult naar breedte. */}
      <div
        role="radiogroup"
        aria-label={ariaLabel || name}
        style={{ display: "grid", gap: "var(--ruimte-4)", gridTemplateColumns: "repeat(6, minmax(44px, 1fr))" }}
      >
        {items.map((n) => {
          const checked = value === n;
          return (
            <label
              key={n}
              style={{ ...KEUZE_BASIS, ...(checked ? KEUZE_AAN : null) }}
              data-testid={`${testid}-option-${n}`}
            >
              <input
                type="radio"
                className="sr-only"
                name={name}
                value={n}
                checked={checked}
                onChange={() => onChange(n)}
              />
              {n}
            </label>
          );
        })}
      </div>
      {hasLabels && (
        <div style={{ marginTop: "var(--ruimte-4)", display: "flex", justifyContent: "space-between", fontSize: "var(--fs-xs)", color: "var(--text-soft)" }}>
          <span>{labels.min ? `${min} = ${labels.min}` : ""}</span>
          <span>{labels.max ? `${max} = ${labels.max}` : ""}</span>
        </div>
      )}
    </div>
  );
}

export function TextField({ name, value, onChange, type = "text", placeholder, testid, ariaLabel, ...rest }) {
  const [focus, setFocus] = React.useState(false);
  return (
    <input
      type={type}
      name={name}
      value={value ?? ""}
      placeholder={placeholder}
      onChange={(e) => onChange(e.target.value)}
      onFocus={() => setFocus(true)}
      onBlur={() => setFocus(false)}
      data-testid={testid}
      aria-label={ariaLabel || name || placeholder || testid}
      style={{
        marginTop: "var(--ruimte-4)", minHeight: "var(--veld-hoogte)", width: "100%",
        borderRadius: "var(--radius)", border: "1px solid " + (focus ? "var(--actie)" : "var(--border-subtle)"),
        background: "var(--wit)", color: "var(--text-strong)",
        padding: "var(--ruimte-4) var(--ruimte-5)",
        // 16px is geen smaak maar noodzaak — zie de kop van dit bestand.
        fontSize: "var(--fs-invoer-mobiel)", fontFamily: "var(--font-tekst)",
        boxShadow: focus ? "var(--schaduw-focus)" : "var(--schaduw-1)",
        outline: "none", transition: "var(--overgang-kleur)",
      }}
      {...rest}
    />
  );
}

export function RadioGroupField({ name, value, onChange, options, testid }) {
  return (
    <div
      role="radiogroup"
      aria-label={name}
      style={{ marginTop: "var(--ruimte-4)", display: "flex", flexWrap: "wrap", gap: "var(--ruimte-4)" }}
      data-testid={testid}
    >
      {options.map((opt) => {
        const checked = value === opt.value;
        return (
          <label
            key={opt.value}
            style={{ ...KEUZE_BASIS, ...(checked ? KEUZE_AAN : null) }}
            data-testid={`${testid}-option-${opt.value}`}
          >
            <input type="radio" className="sr-only" name={name} value={opt.value} checked={checked} onChange={() => onChange(opt.value)} />
            {opt.label}
          </label>
        );
      })}
    </div>
  );
}

export function CheckboxGroupField({ name, value = [], onChange, options, testid, exclusive }) {
  // value = array van strings. options: [{ value, label }]. exclusive = key die solo moet zijn (bv "geen").
  function toggle(optValue) {
    let next;
    if (exclusive && optValue === exclusive) {
      next = value.includes(exclusive) ? [] : [exclusive];
    } else {
      const without = (value || []).filter((v) => v !== exclusive);
      next = without.includes(optValue) ? without.filter((v) => v !== optValue) : [...without, optValue];
    }
    onChange(next);
  }
  return (
    <div
      role="group"
      aria-label={name}
      style={{ marginTop: "var(--ruimte-4)", display: "flex", flexWrap: "wrap", gap: "var(--ruimte-4)" }}
      data-testid={testid}
    >
      {options.map((opt) => {
        const checked = (value || []).includes(opt.value);
        return (
          <label
            key={opt.value}
            style={{ ...KEUZE_BASIS, ...(checked ? KEUZE_AAN : null) }}
            data-testid={`${testid}-option-${opt.value}`}
          >
            <input type="checkbox" className="sr-only" name={name} value={opt.value} checked={checked} onChange={() => toggle(opt.value)} />
            {opt.label}
          </label>
        );
      })}
    </div>
  );
}

export function FieldRow({ label, children, hint, required, error, id }) {
  const heeftFout = !!error;
  return (
    <div
      id={id}
      data-error={heeftFout ? "true" : undefined}
      style={{
        padding: "var(--ruimte-6) 0",
        ...(heeftFout
          ? {
              borderRadius: "var(--radius)", border: "1px solid var(--alarm-rand)",
              background: "var(--alarm-zacht)", padding: "var(--ruimte-6) var(--ruimte-5)",
              margin: "var(--ruimte-3) calc(var(--ruimte-3) * -1)",
            }
          : { borderBottom: "1px solid var(--neutraal-100)" }),
      }}
    >
      <label style={{ display: "block", font: "var(--text-label)", color: "var(--text-strong)" }}>
        {label}
        {required && <span style={{ marginLeft: 3, color: "var(--alarm)" }}>*</span>}
      </label>
      {hint && <p style={{ marginTop: "var(--ruimte-3)", fontSize: "var(--fs-sm)", color: "var(--text-soft)" }}>{hint}</p>}
      <div>{children}</div>
      {heeftFout && typeof error === "string" && (
        <p
          style={{ marginTop: "var(--ruimte-4)", fontSize: "var(--fs-body)", fontWeight: "var(--fw-medium)", color: "var(--text-alarm)" }}
          data-testid={id ? `${id}-fout` : undefined}
        >
          {error}
        </p>
      )}
    </div>
  );
}
